import clsx from 'clsx';
import { RankingIcon, ShirtIcon, StatisticsIcon, TeamIcon } from '@/common/assets';
import { TrackingEvents, useTrack } from '../../tracking';
import { Platforms } from '@/common/constants';
import { usePlatform } from '@/common/hooks';
import { useNewTranslations } from '@/common/translations';
import { Tabs } from '@/ui/components';
import type { TabsOption } from '@/ui/components/Tabs/Tabs.types';
import { MainMenuOptionValues } from '../../constants';
import usePrematchData from '../../hooks/usePrematchData';
import type { MainMenuOption } from '../../types';

interface MainMenuProps {
  /** The currently active tab. */
  activeTab: MainMenuOption;
  /** Callback function to handle tab changes. */
  onActiveTabChange: (tab: MainMenuOption) => void;
}

export default function MainMenu(props: MainMenuProps) {
  const { activeTab, onActiveTabChange } = props;

  const platform = usePlatform();
  const translate = useNewTranslations();
  const { teams } = usePrematchData();
  const track = useTrack();

  const options: TabsOption<MainMenuOption>[] = [
    {
      value: MainMenuOptionValues.STATS,
      label: translate('PreMatchStats.TabStatistics'),
      icon: <StatisticsIcon />,
    },
    {
      value: MainMenuOptionValues.TABLE,
      label: translate('PreMatchStats.TabTable'),
      icon: <RankingIcon />,
    },
    {
      value: MainMenuOptionValues.PLAYERS,
      label: translate('PreMatchStats.TabPlayer'),
      icon: <ShirtIcon />,
    },
  ];

  if (teams.lineupAvailable) {
    options.push({
      value: MainMenuOptionValues.LINEUP,
      label: translate('PreMatchStats.TabLineup'),
      icon: <TeamIcon />,
    });
  }

  const handleTabChange = (tab: MainMenuOption) => {
    const elementOrder = options.findIndex((option) => option.value === tab) + 1;
    track(TrackingEvents.MainTabClick, { tab, elementOrder });
    onActiveTabChange(tab);
  };

  return (
    <div
      className={clsx(
        'relative',
        platform === Platforms.DESKTOP && 'bg-monoY dark:bg-pmw-eb-primary-dark',
      )}
    >
      <Tabs
        options={options}
        value={activeTab}
        platform={platform}
        onValueChange={handleTabChange}
      />
      {platform === Platforms.MOBILE && (
        <div className="absolute top-0 inset-x-0 w-full h-[calc(100%+22px)] bg-pmw-header dark:bg-pmw-header-dark -z-10" />
      )}
    </div>
  );
}
